import React, { useEffect, useState } from "react";
import { api } from "../api";

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
];

function safeText(value) {
  return String(value || "").trim();
}

function validateImage(file, isFR) {
  if (!file) {
    return isFR
      ? "Choisissez d’abord un fichier image."
      : "Choose an image file first.";
  }

  if (
    !ALLOWED_IMAGE_TYPES.includes(
      safeText(file.type).toLowerCase()
    ) ||
    Number(file.size || 0) > MAX_IMAGE_BYTES
  ) {
    return isFR
      ? "Choisissez une image JPG, PNG ou WEBP de 5 Mo maximum."
      : "Choose a JPG, PNG, or WEBP image no larger than 5 MB.";
  }

  return "";
}

export default function StoryDashboardImageSettings({
  story,
  storyId,
  lang = "en",
  onSaved,
}) {
  const isFR = String(lang).toLowerCase().startsWith("fr");

  const storyIdentity = safeText(
    storyId || story?._id || story?.id
  );
  const incomingCoverUrl = safeText(story?.coverImageUrl);
  const incomingAuthorPhotoUrl = safeText(story?.authorPhotoUrl);

  const [coverUrl, setCoverUrl] = useState(incomingCoverUrl);
  const [authorPhotoUrl, setAuthorPhotoUrl] = useState(
    incomingAuthorPhotoUrl
  );

  const [coverFile, setCoverFile] = useState(null);
  const [authorPhotoFile, setAuthorPhotoFile] = useState(null);

  const [coverBusy, setCoverBusy] = useState(false);
  const [authorBusy, setAuthorBusy] = useState(false);
  const [manualSaveBusy, setManualSaveBusy] = useState(false);

  const [coverMessage, setCoverMessage] = useState("");
  const [authorMessage, setAuthorMessage] = useState("");
  const [manualMessage, setManualMessage] = useState("");

  const imageBusy =
    coverBusy || authorBusy || manualSaveBusy;

  useEffect(() => {
    setCoverUrl(incomingCoverUrl);
    setAuthorPhotoUrl(incomingAuthorPhotoUrl);
    setCoverFile(null);
    setAuthorPhotoFile(null);
    setCoverMessage("");
    setAuthorMessage("");
    setManualMessage("");
  }, [
    storyIdentity,
    incomingCoverUrl,
    incomingAuthorPhotoUrl,
  ]);

  async function persistImages(nextCoverUrl, nextAuthorPhotoUrl) {
    if (!storyIdentity) {
      throw new Error(
        isFR
          ? "Storyline introuvable."
          : "Storyline could not be identified."
      );
    }

    const payload = {
      coverImageUrl: safeText(nextCoverUrl),
      authorPhotoUrl: safeText(nextAuthorPhotoUrl),
    };

    const saved = await api.put(
      `/api/stories/${encodeURIComponent(storyIdentity)}`,
      payload
    );

    setCoverUrl(payload.coverImageUrl);
    setAuthorPhotoUrl(payload.authorPhotoUrl);

    if (typeof onSaved === "function") {
      await onSaved(saved);
    }

    return saved;
  }

  async function uploadCover() {
    const validationMessage = validateImage(coverFile, isFR);
    if (validationMessage) {
      setCoverMessage(validationMessage);
      return;
    }

    try {
      setCoverBusy(true);
      setCoverMessage("");
      setManualMessage("");

      const result = await api.uploadStoryCover(coverFile);
      const uploadedUrl = safeText(
        result?.url || result?.publicUrl
      );

      if (!uploadedUrl) {
        throw new Error(
          isFR
            ? "Le téléversement n’a retourné aucune URL."
            : "The upload did not return an image URL."
        );
      }

      setCoverUrl(uploadedUrl);

      await persistImages(uploadedUrl, authorPhotoUrl);

      setCoverFile(null);
      setCoverMessage(
        isFR
          ? "Couverture téléversée et enregistrée ✓"
          : "Cover uploaded and saved ✓"
      );
    } catch (error) {
      setCoverMessage(
        safeText(error?.message) ||
          (isFR
            ? "Impossible de téléverser la couverture."
            : "Could not upload the cover.")
      );
    } finally {
      setCoverBusy(false);
    }
  }

  async function uploadAuthorPhoto() {
    const validationMessage = validateImage(
      authorPhotoFile,
      isFR
    );

    if (validationMessage) {
      setAuthorMessage(validationMessage);
      return;
    }

    try {
      setAuthorBusy(true);
      setAuthorMessage("");
      setManualMessage("");

      const result = await api.uploadAuthorPhoto(
        authorPhotoFile
      );

      const uploadedUrl = safeText(
        result?.url || result?.publicUrl
      );

      if (!uploadedUrl) {
        throw new Error(
          isFR
            ? "Le téléversement n’a retourné aucune URL."
            : "The upload did not return an image URL."
        );
      }

      setAuthorPhotoUrl(uploadedUrl);

      await persistImages(coverUrl, uploadedUrl);

      setAuthorPhotoFile(null);
      setAuthorMessage(
        isFR
          ? "Photo d’auteur téléversée et enregistrée ✓"
          : "Author photo uploaded and saved ✓"
      );
    } catch (error) {
      setAuthorMessage(
        safeText(error?.message) ||
          (isFR
            ? "Impossible de téléverser la photo d’auteur."
            : "Could not upload the author photo.")
      );
    } finally {
      setAuthorBusy(false);
    }
  }

  async function removeCover() {
    if (!coverUrl || imageBusy) return;

    try {
      setCoverBusy(true);
      setCoverMessage("");
      await persistImages("", authorPhotoUrl);
      setCoverFile(null);
      setCoverMessage(
        isFR
          ? "Couverture retirée de cette storyline ✓"
          : "Cover removed from this storyline ✓"
      );
    } catch (error) {
      setCoverMessage(
        safeText(error?.message) ||
          (isFR
            ? "Impossible de retirer la couverture."
            : "Could not remove the cover.")
      );
    } finally {
      setCoverBusy(false);
    }
  }

  async function removeAuthorPhoto() {
    if (!authorPhotoUrl || imageBusy) return;

    try {
      setAuthorBusy(true);
      setAuthorMessage("");
      await persistImages(coverUrl, "");
      setAuthorPhotoFile(null);
      setAuthorMessage(
        isFR
          ? "Photo d’auteur retirée de cette storyline ✓"
          : "Author photo removed from this storyline ✓"
      );
    } catch (error) {
      setAuthorMessage(
        safeText(error?.message) ||
          (isFR
            ? "Impossible de retirer la photo d’auteur."
            : "Could not remove the author photo.")
      );
    } finally {
      setAuthorBusy(false);
    }
  }

  async function saveManualLinks() {
    if (imageBusy) return;

    try {
      setManualSaveBusy(true);
      setManualMessage("");
      await persistImages(coverUrl, authorPhotoUrl);
      setManualMessage(
        isFR
          ? "Liens d’images enregistrés ✓"
          : "Image links saved ✓"
      );
    } catch (error) {
      setManualMessage(
        safeText(error?.message) ||
          (isFR
            ? "Impossible d’enregistrer les liens."
            : "Could not save the image links.")
      );
    } finally {
      setManualSaveBusy(false);
    }
  }

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <p className="ns-card-meta" style={{ margin: 0 }}>
        {isFR
          ? "Les téléversements, remplacements et retraits sont enregistrés immédiatement."
          : "Uploads, replacements, and removals are saved immediately."}
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(270px, 1fr))",
          gap: 16,
        }}
      >
        <section
          className="ns-card ns-card-soft"
          style={{ padding: 16, margin: 0 }}
        >
          <h3 style={{ marginTop: 0 }}>
            {isFR ? "Couverture" : "Story cover"}
          </h3>

          {coverUrl ? (
            <img
              src={coverUrl}
              alt={
                isFR
                  ? "Aperçu de la couverture"
                  : "Story cover preview"
              }
              style={{
                display: "block",
                width: 150,
                maxWidth: "100%",
                aspectRatio: "2 / 3",
                objectFit: "cover",
                marginBottom: 12,
                border: "1px solid rgba(15,23,42,0.15)",
              }}
            />
          ) : (
            <p className="ns-card-meta">
              {isFR
                ? "Aucune couverture sélectionnée."
                : "No story cover selected."}
            </p>
          )}

          <label style={{ display: "grid", gap: 6 }}>
            <strong>
              {isFR
                ? "Choisir une couverture"
                : "Choose story cover image"}
            </strong>
            <input
              type="file"
              aria-label={
                isFR
                  ? "Choisir une couverture"
                  : "Choose story cover image"
              }
              accept="image/jpeg,image/png,image/webp"
              disabled={imageBusy}
              onChange={(event) => {
                setCoverFile(
                  event.target.files?.[0] || null
                );
                setCoverMessage("");
              }}
            />
          </label>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginTop: 10,
            }}
          >
            <button
              type="button"
              className="ns-button ns-button-primary"
              disabled={imageBusy || !coverFile}
              onClick={uploadCover}
            >
              {coverBusy
                ? isFR
                  ? "Téléversement…"
                  : "Uploading…"
                : coverUrl
                  ? isFR
                    ? "Remplacer la couverture"
                    : "Replace cover"
                  : isFR
                    ? "Téléverser la couverture"
                    : "Upload cover"}
            </button>

            <button
              type="button"
              className="ns-button ns-button-outline"
              disabled={imageBusy || !coverUrl}
              onClick={removeCover}
            >
              {isFR
                ? "Retirer de la storyline"
                : "Remove cover from story"}
            </button>
          </div>

          <label
            style={{ display: "grid", gap: 6, marginTop: 14 }}
          >
            <strong>
              {isFR
                ? "URL de la couverture"
                : "Story cover URL"}
            </strong>
            <input
              className="ns-input"
              value={coverUrl}
              placeholder="https://..."
              disabled={imageBusy}
              onChange={(event) => {
                setCoverUrl(event.target.value);
                setCoverMessage("");
                setManualMessage("");
              }}
            />
          </label>

          {coverMessage ? (
            <p className="ns-card-meta" role="status">
              {coverMessage}
            </p>
          ) : null}
        </section>

        <section
          className="ns-card ns-card-soft"
          style={{ padding: 16, margin: 0 }}
        >
          <h3 style={{ marginTop: 0 }}>
            {isFR ? "Photo de l’auteur" : "Author photo"}
          </h3>

          {authorPhotoUrl ? (
            <img
              src={authorPhotoUrl}
              alt={
                isFR
                  ? "Aperçu de la photo d’auteur"
                  : "Author photo preview"
              }
              style={{
                display: "block",
                width: 120,
                height: 120,
                objectFit: "cover",
                borderRadius: "50%",
                marginBottom: 12,
                border: "1px solid rgba(15,23,42,0.15)",
              }}
            />
          ) : (
            <p className="ns-card-meta">
              {isFR
                ? "Aucune photo d’auteur sélectionnée."
                : "No author photo selected."}
            </p>
          )}

          <label style={{ display: "grid", gap: 6 }}>
            <strong>
              {isFR
                ? "Choisir une photo d’auteur"
                : "Choose author photo"}
            </strong>
            <input
              type="file"
              aria-label={
                isFR
                  ? "Choisir une photo d’auteur"
                  : "Choose author photo"
              }
              accept="image/jpeg,image/png,image/webp"
              disabled={imageBusy}
              onChange={(event) => {
                setAuthorPhotoFile(
                  event.target.files?.[0] || null
                );
                setAuthorMessage("");
              }}
            />
          </label>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginTop: 10,
            }}
          >
            <button
              type="button"
              className="ns-button ns-button-primary"
              disabled={imageBusy || !authorPhotoFile}
              onClick={uploadAuthorPhoto}
            >
              {authorBusy
                ? isFR
                  ? "Téléversement…"
                  : "Uploading…"
                : authorPhotoUrl
                  ? isFR
                    ? "Remplacer la photo"
                    : "Replace author photo"
                  : isFR
                    ? "Téléverser la photo"
                    : "Upload author photo"}
            </button>

            <button
              type="button"
              className="ns-button ns-button-outline"
              disabled={imageBusy || !authorPhotoUrl}
              onClick={removeAuthorPhoto}
            >
              {isFR
                ? "Retirer de la storyline"
                : "Remove author photo from story"}
            </button>
          </div>

          <label
            style={{ display: "grid", gap: 6, marginTop: 14 }}
          >
            <strong>
              {isFR
                ? "URL de la photo d’auteur"
                : "Author photo URL"}
            </strong>
            <input
              className="ns-input"
              value={authorPhotoUrl}
              placeholder="https://..."
              disabled={imageBusy}
              onChange={(event) => {
                setAuthorPhotoUrl(event.target.value);
                setAuthorMessage("");
                setManualMessage("");
              }}
            />
          </label>

          {authorMessage ? (
            <p className="ns-card-meta" role="status">
              {authorMessage}
            </p>
          ) : null}
        </section>
      </div>

      <div>
        <button
          type="button"
          className="ns-button ns-button-primary"
          disabled={imageBusy || !storyIdentity}
          onClick={saveManualLinks}
        >
          {manualSaveBusy
            ? isFR
              ? "Enregistrement…"
              : "Saving…"
            : isFR
              ? "Enregistrer les liens d’images"
              : "Save image links"}
        </button>

        {manualMessage ? (
          <p className="ns-card-meta" role="status">
            {manualMessage}
          </p>
        ) : null}
      </div>

      <p className="ns-card-meta" role="note" style={{ margin: 0 }}>
        {isFR
          ? "Retirer une image enlève son association avec cette storyline. L’ancien fichier n’est pas supprimé physiquement du stockage."
          : "Removing an image clears its association with this storyline. The old file is not physically deleted from storage."}
      </p>
    </div>
  );
}
